import React, { useState, useEffect, useRef } from 'react';
import { Spinner, Modal, Form, Button, Alert, Card, Row, Col, Dropdown, Badge, ListGroup } from 'react-bootstrap';
import { useAuth } from '../../hooks/useAuth';
import { agentApi, notificationApi, searchApi } from '../../services/api';
import { FaUser, FaKey, FaBell, FaShieldAlt, FaFileAlt, FaIdCard, FaFileInvoice, FaUniversity, FaSignOutAlt, FaSearch, FaTimes, FaQuestionCircle, FaUpload, FaCheck } from 'react-icons/fa';
import AgentSidebar from '../../components/AgentSidebar';
import SearchResults from '../../components/SearchResults';
import './Settings.css'; // Using DSO Settings CSS with overrides
import { useNavigate } from 'react-router-dom';

function AgentSettings() {
  const navigate = useNavigate();
  const { getCurrentUser, logout } = useAuth();
  const currentUser = getCurrentUser();
  
  // --- Search State ---
  const [searchQuery, setSearchQuery] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const searchRef = useRef(null);
  // --- End Search State ---
  
  // --- Notification State ---
  const [notifications, setNotifications] = useState([]);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false); // Controls dropdown visibility
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const notificationRef = useRef(null);
  
  // Notification handlers
  const toggleNotifications = () => {
    setShowNotifications(prev => !prev);
  };
  
  const handleMarkAsRead = async (notificationId) => {
    const notification = notifications.find(n => n.id === notificationId);
    if (!notification || notification.is_read) return;

    try {
      await notificationApi.markAsRead(notificationId);
      setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };
  
  const handleMarkAllAsRead = async () => {
    if (unreadCount === 0) return;

    try {
      await notificationApi.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };
  
  const formatTimestamp = (timestamp) => {
    try {
      return new Date(timestamp).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
    } catch (e) { return timestamp; }
  };
  // --- End Notification State ---
  const [activeSection, setActiveSection] = useState('profile');
  const [agentDetails, setAgentDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // --- Document Upload State ---
  const [currentDocumentType, setCurrentDocumentType] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadLoading, setUploadLoading] = useState({
    adhaar_front: false,
    adhaar_back: false,
    pan_card: false
  });
  const [uploadSuccess, setUploadSuccess] = useState({
    adhaar_front: false,
    adhaar_back: false,
    pan_card: false
  });
  const [uploadError, setUploadError] = useState({
    adhaar_front: '',
    adhaar_back: '',
    pan_card: ''
  });
  const fileInputRefs = {
    adhaar_front: useRef(null),
    adhaar_back: useRef(null),
    pan_card: useRef(null)
  };
  // --- End Document Upload State ---
  
  // Form data for editing profile
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    profile_data: {
      address: '',
      adhaar_card_no: '',
      pan_card_no: '',
      city: '',
      state: ''
    },
    bank_details: {
      bank_name: '',
      bank_account_no: '',
      bank_ifsc_code: '',
      bank_branch_name: '',
      bank_account_type: '1', // Default to Savings Account
      upi_id: ''
    }
  });
  const [updateSuccess, setUpdateSuccess] = useState('');
  const [updateError, setUpdateError] = useState('');
  const [saveLoading, setSaveLoading] = useState(false);
  
  // Add a new state for bank form submission
  const [bankUpdateSuccess, setBankUpdateSuccess] = useState('');
  const [bankUpdateError, setBankUpdateError] = useState('');
  const [bankSaveLoading, setBankSaveLoading] = useState(false);

  // Password reset modal state
  const [showResetModal, setShowResetModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState('');
  const [resetSuccess, setResetSuccess] = useState('');

  // Modal handler functions
  const handleCloseModal = () => {
    setShowResetModal(false);
    // Reset modal-specific states
    setNewPassword('');
    setConfirmPassword('');
    setResetError('');
    setResetSuccess('');
    setResetLoading(false);
  };

  const handlePasswordResetSubmit = async (event) => {
    event.preventDefault();
    setResetError('');
    setResetSuccess('');

    if (newPassword !== confirmPassword) {
      setResetError('Passwords do not match.');
      return;
    }
    if (newPassword.length < 5) {
      setResetError('Password must be at least 5 characters long.');
      return;
    }

    setResetLoading(true);
    try {
      await agentApi.changePassword(newPassword);
      setResetSuccess('Password reset successfully!');
    } catch (err) {
      console.error("Error resetting password:", err);
      // Check if the error response contains validation errors (an object) and convert to string
      if (err.response?.data?.detail && typeof err.response.data.detail === 'object') {
        // Convert the validation error object to a readable string
        try {
          const errorMessage = JSON.stringify(err.response.data.detail);
          setResetError(`Validation error: ${errorMessage}`);
        } catch (e) {
          setResetError('Invalid data format. Please check your inputs.');
        }
      } else {
        // Handle regular string error messages
        setResetError(err.response?.data?.detail || 'Failed to reset password. Please try again.');
      }
    } finally {
      setResetLoading(false);
    }
  };

  // Navigation items for settings
  const navItems = [
    { id: 'profile', label: 'Profile Information', icon: <FaUser size={16} /> },
    { id: 'documents', label: 'Documents', icon: <FaFileAlt size={16} /> },
    { id: 'bank', label: 'Bank Details', icon: <FaUniversity size={16} /> },
    { id: 'security', label: 'Security', icon: <FaKey size={16} /> },
    { id: 'notifications', label: 'Notifications', icon: <FaBell size={16} /> }
  ];
  
  // Toggle handlers for notifications
  const toggleNotificationsEnabledSetting = () => {
    setNotificationsEnabled(!notificationsEnabled);
  };


  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name.includes('.')) {
      // Handle nested fields (profile_data or bank_details)
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      // Handle top-level fields
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };
  
  // Submit profile update
  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setUpdateError('');
    setUpdateSuccess('');
    setSaveLoading(true);

    try {
      const response = await agentApi.updateAgentProfile(formData);
      setAgentDetails(response.data);
      setUpdateSuccess('Profile updated successfully!');
      
      // Hide success message after 3 seconds
      setTimeout(() => {
        setUpdateSuccess('');
      }, 3000);
    } catch (err) {
      console.error("Error updating profile:", err);
      // Check if the error response contains validation errors (an object) and convert to string
      if (err.response?.data?.detail && typeof err.response.data.detail === 'object') {
        // Convert the validation error object to a readable string
        try {
          const errorMessage = JSON.stringify(err.response.data.detail);
          setUpdateError(`Validation error: ${errorMessage}`);
        } catch (e) {
          setUpdateError('Invalid data format. Please check your inputs.');
        }
      } else {
        // Handle regular string error messages
        setUpdateError(err.response?.data?.detail || 'Failed to update profile. Please try again.');
      }
    } finally {
      setSaveLoading(false);
    }
  };
  
  // Handle bank details form submission
  const handleBankDetailsSubmit = async (e) => {
    e.preventDefault();
    setBankUpdateError('');
    setBankUpdateSuccess('');
    setBankSaveLoading(true);

    try {
      const response = await agentApi.updateAgentBankDetails(formData.bank_details);
      console.log("Bank details updated:", response.data);
      
      // Update the local state with the new data
      setAgentDetails({
        ...agentDetails,
        agent_profile: {
          ...agentDetails.agent_profile,
          ...response.data
        }
      });
      
      setBankUpdateSuccess('Bank details updated successfully!');
      
      // Hide success message after 3 seconds
      setTimeout(() => {
        setBankUpdateSuccess('');
      }, 3000);
    } catch (err) {
      console.error("Error updating bank details:", err);
      // Check if the error response contains validation errors (an object) and convert to string
      if (err.response?.data?.detail && typeof err.response.data.detail === 'object') {
        // Convert the validation error object to a readable string
        try {
          const errorMessage = JSON.stringify(err.response.data.detail);
          setBankUpdateError(`Validation error: ${errorMessage}`);
        } catch (e) {
          setBankUpdateError('Invalid data format. Please check your inputs.');
        }
      } else {
        // Handle regular string error messages
        setBankUpdateError(err.response?.data?.detail || 'Failed to update bank details. Please try again.');
      }
    } finally {
      setBankSaveLoading(false);
    }
  };

  // Fetch Agent details
  useEffect(() => {
    const fetchDetails = async () => {
      setLoading(true);
      setError('');
      try {
        console.log("Attempting to fetch Agent details...");
        console.log("Calling API:", agentApi);
        const response = await agentApi.getAgentProfile();
        console.log("Agent details fetched:", response.data);
        setAgentDetails(response.data);
        
        // Initialize form data with fetched details
        if (response.data) {
          setFormData({
            name: response.data.name || '',
            mobile: response.data.mobile || '',
            profile_data: {
              address: response.data.agent_profile?.address || '',
              adhaar_card_no: response.data.agent_profile?.adhaar_card_no || '',
              pan_card_no: response.data.agent_profile?.pan_card_no || '',
              city: response.data.agent_profile?.city_name || '',
              state: response.data.agent_profile?.state_name || ''
            },
            bank_details: {
              bank_name: response.data.agent_profile?.bank_name || '',
              bank_account_no: response.data.agent_profile?.bank_account_no || '',
              bank_ifsc_code: response.data.agent_profile?.bank_ifsc_code || '',
              bank_branch_name: response.data.agent_profile?.bank_branch_name || '',
              bank_account_type: response.data.agent_profile?.bank_account_type?.toString() || '1',
              upi_id: response.data.agent_profile?.upi_id || ''
            }
          });
        }
        
        // If your API returns notification preferences, set them here
        if (response.data.preferences) {
          setNotificationsEnabled(response.data.preferences.notificationEnabled || true);
        }
      } catch (err) {
        console.error("Error fetching Agent details:", err);
        let errorMessage = 'Failed to load profile information.';
        if (err.response) {
          console.error("Error response:", err.response.data);
          errorMessage += ` (Server: ${err.response.data.detail || err.response.statusText})`;
        } else if (err.request) {
          console.error("Error request:", err.request);
          errorMessage += ' (No response from server)';
        } else {
          console.error("Error message:", err.message);
          errorMessage += ` (${err.message})`;
        }
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, []);

  // Monitor modal state for debugging
  useEffect(() => {
    console.log(`showResetModal state changed to: ${showResetModal}`);
  }, [showResetModal]);
  
  // Notifications polling effect
  useEffect(() => {
    let intervalId;
    const fetchNotificationsData = async () => {
      try {
        const countResponse = await notificationApi.getUnreadCount();
        setUnreadCount(countResponse.data.unread_count);
      } catch (error) {
        console.error('Error fetching notifications count:', error);
      }
    };

    fetchNotificationsData(); // Initial fetch
    intervalId = setInterval(fetchNotificationsData, 60000); // Poll count every minute
    
    return () => clearInterval(intervalId); // Cleanup
  }, []);

  // Fetch full notifications when dropdown opens
  useEffect(() => {
    const fetchFullNotifications = async () => {
      if (showNotifications) {
        setLoadingNotifications(true);
        try {
          const notificationsResponse = await notificationApi.getNotifications({ limit: 10 });
          setNotifications(notificationsResponse.data);
        } catch (error) {
          console.error('Error fetching notifications:', error);
          setNotifications([]);
        } finally {
          setLoadingNotifications(false);
        }
      }
    };
    fetchFullNotifications();
  }, [showNotifications]);

  // Handle clicks outside notification dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Document upload handlers
  const handleDocumentFileChange = (e, documentType) => {
    const file = e.target.files[0];
    if (!file) return;

    setCurrentDocumentType(documentType);
    setSelectedFile(file);
    
    // Reset status states
    setUploadSuccess(prev => ({
      ...prev,
      [documentType]: false
    }));
    setUploadError(prev => ({
      ...prev,
      [documentType]: ''
    }));
    
    // Set loading state for this document type
    setUploadLoading(prev => ({
      ...prev,
      [documentType]: true
    }));
    
    // Proceed with upload
    handleDocumentUpload(file, documentType);
  };

  const handleDocumentUpload = async (file, documentType) => {
    try {
      // Create form data
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', documentType);
      
      // Make API call
      const response = await agentApi.uploadDocument(formData);
      
      console.log('Document upload response:', response.data);
      
      // Update success state
      setUploadSuccess(prev => ({
        ...prev,
        [documentType]: true
      }));
      
      // Update the agent details with the new file URL
      if (response.data) {
        const updatedAgentDetails = { ...agentDetails };
        
        // Map document type to the corresponding profile property and get URL from response
        switch(documentType) {
          case 'adhaar_front':
            updatedAgentDetails.agent_profile.adhaar_card_front_file = 
              response.data.file_path || response.data.url || response.data.document_url;
            break;
          case 'adhaar_back':
            updatedAgentDetails.agent_profile.adhaar_card_back_file = 
              response.data.file_path || response.data.url || response.data.document_url;
            break;
          case 'pan_card':
            updatedAgentDetails.agent_profile.pan_card_file = 
              response.data.file_path || response.data.url || response.data.document_url;
            break;
          default:
            break;
        }
        
        // Refresh agent profile to ensure we have the latest data
        agentApi.getAgentProfile()
          .then(refreshResponse => {
            if (refreshResponse && refreshResponse.data) {
              setAgentDetails(refreshResponse.data);
              console.log('Agent profile refreshed after document upload');
            }
          })
          .catch(error => {
            console.error('Error refreshing agent profile:', error);
          });
        
        // Also update current state
        setAgentDetails(updatedAgentDetails);
      }
      
      // Hide success message after 3 seconds
      setTimeout(() => {
        setUploadSuccess(prev => ({
          ...prev,
          [documentType]: false
        }));
      }, 3000);
      
    } catch (err) {
      console.error(`Error uploading ${getDocumentTypeLabel(documentType)}:`, err);
      setUploadError(prev => ({
        ...prev,
        [documentType]: err.response?.data?.detail || `Failed to upload document. Please try again.`
      }));
      
      // Hide error message after 5 seconds
      setTimeout(() => {
        setUploadError(prev => ({
          ...prev,
          [documentType]: ''
        }));
      }, 5000);
    } finally {
      setUploadLoading(prev => ({
        ...prev,
        [documentType]: false
      }));
      
      // Reset file input
      if (fileInputRefs[documentType]?.current) {
        fileInputRefs[documentType].current.value = '';
      }
    }
  };
  
  // Helper function to get user-friendly document type labels
  const getDocumentTypeLabel = (documentType) => {
    switch(documentType) {
      case 'adhaar_front': return 'Aadhaar Card (Front)';
      case 'adhaar_back': return 'Aadhaar Card (Back)';
      case 'pan_card': return 'PAN Card';
      default: return 'Document';
    }
  };

  // Render content based on active section
  const renderContent = () => {
    switch (activeSection) {
      case 'profile':
        if (loading) {
          return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100px' }}>
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </div>
          );
        }
        
        if (error) {
          return <p style={{ color: 'red' }}>Error: {error}</p>;
        }
        
        return (
          <>
            <h3><FaUser className="me-2" /> Profile Information</h3>
            
            {updateSuccess && <Alert variant="success">{updateSuccess}</Alert>}
            {updateError && <Alert variant="danger">{updateError}</Alert>}
            
            {agentDetails ? (
              <Form onSubmit={handleProfileSubmit}>
                <div className="dso-profile-details">
                  <Card className="mb-4">
                    <Card.Header className="bg-light">
                      <h5 className="mb-0 d-flex align-items-center">
                        <FaUser className="me-2" /> Personal Information
                      </h5>
                    </Card.Header>
                    <Card.Body>
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Full Name</Form.Label>
                            <Form.Control 
                              type="text"
                              name="name"
                              value={formData.name}
                              onChange={handleInputChange}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Email Address</Form.Label>
                            <Form.Control
                              type="email"
                              value={agentDetails.email || ''}
                              readOnly
                              disabled
                              className="bg-light"
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Mobile Number</Form.Label>
                            <Form.Control 
                              type="text"
                              name="mobile"
                              value={formData.mobile}
                              onChange={handleInputChange}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Status</Form.Label>
                            <Form.Control
                              type="text"
                              value={agentDetails.agent_profile?.status || (agentDetails.is_active ? 'Active' : 'Inactive')}
                              readOnly
                              disabled
                              className="bg-light"
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                  
                  <Card className="mb-4">
                    <Card.Header className="bg-light">
                      <h5 className="mb-0 d-flex align-items-center">
                        <FaIdCard className="me-2" /> Location Details
                      </h5>
                    </Card.Header>
                    <Card.Body>
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>City</Form.Label>
                            <Form.Control 
                              type="text"
                              name="profile_data.city"
                              value={formData.profile_data.city}
                              onChange={handleInputChange}
                              disabled
                              title="Contact DSO to update city"
                              className="bg-light"
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>State</Form.Label>
                            <Form.Control 
                              type="text"
                              name="profile_data.state"
                              value={formData.profile_data.state}
                              onChange={handleInputChange}
                              disabled
                              title="Contact DSO to update state"
                              className="bg-light"
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                      <Row>
                        <Col md={12}>
                          <Form.Group className="mb-3">
                            <Form.Label>Address</Form.Label>
                            <Form.Control 
                              as="textarea"
                              rows={3}
                              name="profile_data.address"
                              value={formData.profile_data.address}
                              onChange={handleInputChange}
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                  
                  <Card className="mb-4">
                    <Card.Header className="bg-light">
                      <h5 className="mb-0 d-flex align-items-center">
                        <FaIdCard className="me-2" /> Identity Details
                      </h5>
                    </Card.Header>
                    <Card.Body>
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Aadhaar Card Number</Form.Label>
                            <Form.Control 
                              type="text"
                              name="profile_data.adhaar_card_no"
                              value={formData.profile_data.adhaar_card_no}
                              onChange={handleInputChange}
                              maxLength={12}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>PAN Card Number</Form.Label>
                            <Form.Control 
                              type="text"
                              name="profile_data.pan_card_no"
                              value={formData.profile_data.pan_card_no}
                              onChange={handleInputChange}
                              maxLength={10}
                              style={{ textTransform: 'uppercase' }}
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                  
                  <div className="mt-4 d-flex justify-content-end">
                    <Button 
                      type="submit" 
                      className="dso-btn dso-btn-primary" 
                      disabled={saveLoading}
                    >
                      {saveLoading ? (
                        <>
                          <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> Saving...
                        </>
                      ) : "Save Changes"}
                    </Button>
                  </div>
                </div>
              </Form>
            ) : (
              <p>Could not load profile data.</p>
            )}
          </>
        );
        
      case 'documents':
        return (
          <>
            <h3><FaFileAlt className="me-2" /> Your Documents</h3>
            <div className="dso-document-grid">
              {agentDetails ? (
                <Row className="g-3">
                  <Col md={6}>
                    <Card className="dso-document-card h-100">
                      <Card.Body>
                        <Card.Title><FaIdCard size={16} color="#0a2540" /> Aadhaar Card (Front)</Card.Title>
                        {agentDetails.agent_profile?.adhaar_card_front_file ? (
                          <>
                            <div className="document-status document-uploaded">
                              Document Uploaded
                              <Button 
                                variant="outline-primary"
                                size="sm"
                                className="ms-2 upload-btn"
                                onClick={() => fileInputRefs.adhaar_front.current.click()}
                                disabled={uploadLoading.adhaar_front}
                              >
                                {uploadLoading.adhaar_front ? (
                                  <Spinner animation="border" size="sm" />
                                ) : (
                                  <>
                                    <FaUpload className="me-1" /> Replace
                                  </>
                                )}
                              </Button>
                              <input
                                type="file"
                                ref={fileInputRefs.adhaar_front}
                                style={{ display: 'none' }}
                                accept="image/jpeg,image/png,application/pdf"
                                onChange={(e) => handleDocumentFileChange(e, 'adhaar_front')}
                              />
                            </div>
                            {uploadSuccess.adhaar_front && (
                              <Alert variant="success" className="mt-2 py-1 px-2 small">
                                <FaCheck className="me-1" /> Document updated successfully
                              </Alert>
                            )}
                            {uploadError.adhaar_front && (
                              <Alert variant="danger" className="mt-2 py-1 px-2 small">
                                {uploadError.adhaar_front}
                              </Alert>
                            )}
                            <div className="document-preview">
                              {agentDetails.agent_profile.adhaar_card_front_file.toLowerCase().endsWith('.pdf') ? (
                                <a 
                                  href={agentDetails.agent_profile.adhaar_card_front_file} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="pdf-link"
                                >
                                  <FaFileAlt size={40} />
                                  <div>View PDF Document</div>
                                </a>
                              ) : (
                                <img 
                                  src={agentDetails.agent_profile.adhaar_card_front_file} 
                                  alt="Aadhaar Front" 
                                  className="document-thumbnail" 
                                />
                              )}
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="document-status document-missing">
                              Not Uploaded
                              <Button 
                                variant="primary"
                                size="sm"
                                className="ms-2 upload-btn"
                                onClick={() => fileInputRefs.adhaar_front.current.click()}
                                disabled={uploadLoading.adhaar_front}
                              >
                                {uploadLoading.adhaar_front ? (
                                  <Spinner animation="border" size="sm" />
                                ) : (
                                  <>
                                    <FaUpload className="me-1" /> Upload
                                  </>
                                )}
                              </Button>
                              <input
                                type="file"
                                ref={fileInputRefs.adhaar_front}
                                style={{ display: 'none' }}
                                accept="image/jpeg,image/png,application/pdf"
                                onChange={(e) => handleDocumentFileChange(e, 'adhaar_front')}
                              />
                            </div>
                            {uploadSuccess.adhaar_front && (
                              <Alert variant="success" className="mt-2 py-1 px-2 small">
                                <FaCheck className="me-1" /> Document uploaded successfully
                              </Alert>
                            )}
                            {uploadError.adhaar_front && (
                              <Alert variant="danger" className="mt-2 py-1 px-2 small">
                                {uploadError.adhaar_front}
                              </Alert>
                            )}
                          </>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                  
                  <Col md={6}>
                    <Card className="dso-document-card h-100">
                      <Card.Body>
                        <div className="document-icon">
                          
                        </div>
                        <Card.Title><FaIdCard size={16} color="#0a2540" /> Aadhaar Card (Back)</Card.Title>
                        {agentDetails.agent_profile?.adhaar_card_back_file ? (
                          <>
                            <div className="document-status document-uploaded">
                              Document Uploaded
                              <Button 
                                variant="outline-primary"
                                size="sm"
                                className="ms-2 upload-btn"
                                onClick={() => fileInputRefs.adhaar_back.current.click()}
                                disabled={uploadLoading.adhaar_back}
                              >
                                {uploadLoading.adhaar_back ? (
                                  <Spinner animation="border" size="sm" />
                                ) : (
                                  <>
                                    <FaUpload className="me-1" /> Replace
                                  </>
                                )}
                              </Button>
                              <input
                                type="file"
                                ref={fileInputRefs.adhaar_back}
                                style={{ display: 'none' }}
                                accept="image/jpeg,image/png,application/pdf"
                                onChange={(e) => handleDocumentFileChange(e, 'adhaar_back')}
                              />
                            </div>
                            {uploadSuccess.adhaar_back && (
                              <Alert variant="success" className="mt-2 py-1 px-2 small">
                                <FaCheck className="me-1" /> Document updated successfully
                              </Alert>
                            )}
                            {uploadError.adhaar_back && (
                              <Alert variant="danger" className="mt-2 py-1 px-2 small">
                                {uploadError.adhaar_back}
                              </Alert>
                            )}
                            <div className="document-preview">
                              {agentDetails.agent_profile.adhaar_card_back_file.toLowerCase().endsWith('.pdf') ? (
                                <a 
                                  href={agentDetails.agent_profile.adhaar_card_back_file} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="pdf-link"
                                >
                                  <FaFileAlt size={40} />
                                  <div>View PDF Document</div>
                                </a>
                              ) : (
                                <img 
                                  src={agentDetails.agent_profile.adhaar_card_back_file} 
                                  alt="Aadhaar Back" 
                                  className="document-thumbnail" 
                                />
                              )}
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="document-status document-missing">
                              Not Uploaded
                              <Button 
                                variant="primary"
                                size="sm"
                                className="ms-2 upload-btn"
                                onClick={() => fileInputRefs.adhaar_back.current.click()}
                                disabled={uploadLoading.adhaar_back}
                              >
                                {uploadLoading.adhaar_back ? (
                                  <Spinner animation="border" size="sm" />
                                ) : (
                                  <>
                                    <FaUpload className="me-1" /> Upload
                                  </>
                                )}
                              </Button>
                              <input
                                type="file"
                                ref={fileInputRefs.adhaar_back}
                                style={{ display: 'none' }}
                                accept="image/jpeg,image/png,application/pdf"
                                onChange={(e) => handleDocumentFileChange(e, 'adhaar_back')}
                              />
                            </div>
                            {uploadSuccess.adhaar_back && (
                              <Alert variant="success" className="mt-2 py-1 px-2 small">
                                <FaCheck className="me-1" /> Document uploaded successfully
                              </Alert>
                            )}
                            {uploadError.adhaar_back && (
                              <Alert variant="danger" className="mt-2 py-1 px-2 small">
                                {uploadError.adhaar_back}
                              </Alert>
                            )}
                          </>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                  
                  <Col md={6}>
                    <Card className="dso-document-card h-100">
                      <Card.Body>
                        <Card.Title><FaFileInvoice size={16} color="#0a2540" /> PAN Card</Card.Title>
                        {agentDetails.agent_profile?.pan_card_file ? (
                          <>
                            <div className="document-status document-uploaded">
                              Document Uploaded
                              {agentDetails.agent_profile?.pan_verified && 
                                <span className="verification-badge">Verified</span>
                              }
                              <Button 
                                variant="outline-primary"
                                size="sm"
                                className="ms-2 upload-btn"
                                onClick={() => fileInputRefs.pan_card.current.click()}
                                disabled={uploadLoading.pan_card || agentDetails.agent_profile?.pan_verified}
                              >
                                {uploadLoading.pan_card ? (
                                  <Spinner animation="border" size="sm" />
                                ) : (
                                  <>
                                    <FaUpload className="me-1" /> Replace
                                  </>
                                )}
                              </Button>
                              <input
                                type="file"
                                ref={fileInputRefs.pan_card}
                                style={{ display: 'none' }}
                                accept="image/jpeg,image/png,application/pdf"
                                onChange={(e) => handleDocumentFileChange(e, 'pan_card')}
                              />
                            </div>
                            {uploadSuccess.pan_card && (
                              <Alert variant="success" className="mt-2 py-1 px-2 small">
                                <FaCheck className="me-1" /> Document updated successfully
                              </Alert>
                            )}
                            {uploadError.pan_card && (
                              <Alert variant="danger" className="mt-2 py-1 px-2 small">
                                {uploadError.pan_card}
                              </Alert>
                            )}
                            <div className="document-preview">
                              {agentDetails.agent_profile.pan_card_file.toLowerCase().endsWith('.pdf') ? (
                                <a 
                                  href={agentDetails.agent_profile.pan_card_file} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="pdf-link"
                                >
                                  <FaFileAlt size={40} />
                                  <div>View PDF Document</div>
                                </a>
                              ) : (
                                <img 
                                  src={agentDetails.agent_profile.pan_card_file} 
                                  alt="PAN Card" 
                                  className="document-thumbnail" 
                                />
                              )}
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="document-status document-missing">
                              Not Uploaded
                              <Button 
                                variant="primary"
                                size="sm"
                                className="ms-2 upload-btn"
                                onClick={() => fileInputRefs.pan_card.current.click()}
                                disabled={uploadLoading.pan_card}
                              >
                                {uploadLoading.pan_card ? (
                                  <Spinner animation="border" size="sm" />
                                ) : (
                                  <>
                                    <FaUpload className="me-1" /> Upload
                                  </>
                                )}
                              </Button>
                              <input
                                type="file"
                                ref={fileInputRefs.pan_card}
                                style={{ display: 'none' }}
                                accept="image/jpeg,image/png,application/pdf"
                                onChange={(e) => handleDocumentFileChange(e, 'pan_card')}
                              />
                            </div>
                            {uploadSuccess.pan_card && (
                              <Alert variant="success" className="mt-2 py-1 px-2 small">
                                <FaCheck className="me-1" /> Document uploaded successfully
                              </Alert>
                            )}
                            {uploadError.pan_card && (
                              <Alert variant="danger" className="mt-2 py-1 px-2 small">
                                {uploadError.pan_card}
                              </Alert>
                            )}
                          </>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              ) : (
                <p>Could not load document data.</p>
              )}
            </div>
          </>
        );
        
      case 'security':
        console.log("Rendering 'security' section content.");
        return (
          <>
            <h3><FaShieldAlt className="me-2" /> Security Settings</h3>
            <div className="dso-profile-details">
              <div className="dso-detail-item">
                <span className="dso-detail-label">Password:</span>
                <button
                  className="dso-btn dso-btn-primary"
                  onClick={() => {
                    console.log("Reset Password button clicked!");
                    setShowResetModal(true);
                  }}
                >
                  <FaKey/> Reset Password
                </button>
              </div>
            </div>
          </>
        );
        
      case 'notifications':
        return (
          <>
            <h3><FaBell className="me-2" /> Notification Preferences</h3>
            <div className="dso-profile-details">
              <div className="dso-detail-item">
                <span className="dso-detail-label">Notifications:</span>
                <div className="dso-toggle-container">
                  <label className="dso-toggle">
                    <input 
                      type="checkbox" 
                      checked={notificationsEnabled} 
                      onChange={toggleNotificationsEnabledSetting} 
                    />
                    <span className="dso-toggle-slider"></span>
                  </label>
                  <span className="dso-toggle-status">
                    {notificationsEnabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>
            </div>
          </>
        );
       
      case 'bank':
        if (loading) {
          return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100px' }}>
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </div>
          );
        }
        
        if (error) {
          return <p style={{ color: 'red' }}>Error: {error}</p>;
        }
        
        return (
          <>
            <h3><FaUniversity className="me-2" /> Bank Account Details</h3>
            
            {bankUpdateSuccess && <Alert variant="success">{bankUpdateSuccess}</Alert>}
            {bankUpdateError && <Alert variant="danger">{bankUpdateError}</Alert>}
            
            <Form onSubmit={handleBankDetailsSubmit}>
              <div className="dso-profile-details">
                <Card className="mb-4">
                  <Card.Header className="bg-light">
                    <h5 className="mb-0 d-flex align-items-center">
                      <FaUniversity className="me-2" /> Bank Information
                    </h5>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Bank Name</Form.Label>
                          <Form.Control 
                            type="text"
                            name="bank_details.bank_name"
                            value={formData.bank_details.bank_name}
                            onChange={handleInputChange}
                            placeholder="Enter bank name"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Branch Name</Form.Label>
                          <Form.Control 
                            type="text"
                            name="bank_details.bank_branch_name"
                            value={formData.bank_details.bank_branch_name}
                            onChange={handleInputChange}
                            placeholder="Enter branch name"
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Account Number</Form.Label>
                          <Form.Control 
                            type="text"
                            name="bank_details.bank_account_no"
                            value={formData.bank_details.bank_account_no}
                            onChange={handleInputChange}
                            placeholder="Enter account number"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>IFSC Code</Form.Label>
                          <Form.Control 
                            type="text"
                            name="bank_details.bank_ifsc_code"
                            value={formData.bank_details.bank_ifsc_code}
                            onChange={handleInputChange}
                            placeholder="Enter IFSC code"
                            style={{ textTransform: 'uppercase' }}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Account Type</Form.Label>
                          <Form.Select
                            name="bank_details.bank_account_type"
                            value={formData.bank_details.bank_account_type}
                            onChange={handleInputChange}
                          >
                            <option value="1">Savings Account</option>
                            <option value="2">Current Account</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>UPI ID</Form.Label>
                          <Form.Control 
                            type="text"
                            name="bank_details.upi_id"
                            value={formData.bank_details.upi_id}
                            onChange={handleInputChange}
                            placeholder="Enter UPI ID (optional)"
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <div className="alert alert-info mt-3">
                      <small>
                        <strong>Note:</strong> Bank account details are required for commission payouts. 
                        Please ensure the information is accurate as it will be verified.
                      </small>
                    </div>
                  </Card.Body>
                </Card>
                
                <div className="mt-4 d-flex justify-content-end">
                  <Button 
                    type="submit" 
                    className="dso-btn dso-btn-primary" 
                    disabled={bankSaveLoading}
                  >
                    {bankSaveLoading ? (
                      <>
                        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> Saving...
                      </>
                    ) : "Save Bank Details"}
                  </Button>
                </div>
              </div>
            </Form>
          </>
        );
      default:
        return <p>Select a section from the menu</p>;
    }
  };

  // Search handler functions
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    // Clear previous timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    if (query.trim().length >= 2) {
      // Set a new timeout for search
      const timeout = setTimeout(() => {
        performSearch(query);
      }, 500); // Debounce 500ms
      setSearchTimeout(timeout);
    } else {
      setShowSearchResults(false);
      setSearchResults([]);
    }
  };
  
  const performSearch = async (query) => {
    if (!query.trim() || query.length < 2) return;
    
    setIsSearching(true);
    try {
      const response = await searchApi.globalSearch(query, { limit_per_type: 5 });
      setSearchResults(response.data.results || []);
      setShowSearchResults(true);
    } catch (error) {
      console.error('Error performing search:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };
  
  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setShowSearchResults(false);
  };

  // Note: All notification handlers are defined earlier in the component
  
  // Handle logout
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Fetch notifications count
  useEffect(() => {
    let intervalId;
    const fetchNotificationsData = async () => {
      try {
        const countResponse = await notificationApi.getUnreadCount();
        setUnreadCount(countResponse.data.unread_count);
      } catch (error) {
        console.error('Error fetching notifications count:', error);
      }
    };

    fetchNotificationsData(); // Initial fetch
    intervalId = setInterval(fetchNotificationsData, 60000); // Poll count every minute
    
    return () => clearInterval(intervalId); // Cleanup
  }, []);

  // Fetch full notifications when dropdown opens
  useEffect(() => {
    const fetchFullNotifications = async () => {
      if (showNotifications) {
        setLoadingNotifications(true);
        try {
          const notificationsResponse = await notificationApi.getNotifications({ limit: 10 });
          setNotifications(notificationsResponse.data);
        } catch (error) {
          console.error('Error fetching notifications:', error);
          setNotifications([]);
        } finally {
          setLoadingNotifications(false);
        }
      }
    };
    fetchFullNotifications();
  }, [showNotifications]);

  // Handle clicks outside notification dropdown and search results
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
      
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="agent-dashboard-layout">
      <AgentSidebar />
      
      <div className="agent-content-wrapper">
        <header className="agent-header">
          <div className="agent-greeting">
            <span className="agent-greeting-text">Hi, {currentUser?.name || 'Agent'} <span className="agent-greeting-emoji">👋</span></span>
          </div>

          <div ref={searchRef} className="agent-header-search">
            <div className="search-input-container">
              <FaSearch className="search-icon" />
              <input 
                type="text" 
                placeholder="Search leads, products, invoices..."
                value={searchQuery}
                onChange={handleSearchChange}
                onFocus={() => {
                  if (searchResults.length > 0) {
                    setShowSearchResults(true);
                  }
                }}
              />
              {searchQuery && (
                <button className="clear-search-btn" onClick={clearSearch}>
                  <FaTimes />
                </button>
              )}
              {isSearching && (
                <Spinner 
                  animation="border" 
                  size="sm" 
                  className="search-spinner" 
                />
              )}
            </div>
            
            {/* Search results dropdown */}
            {showSearchResults && searchResults.length > 0 && (
              <div className="search-results-dropdown">
                <SearchResults 
                  results={searchResults}
                  onResultClick={() => setShowSearchResults(false)}
                />
              </div>
            )}
          </div>

          <div className="agent-header-actions">
            <div className="agent-header-icon">
              <FaQuestionCircle size={18} />
            </div>
            
            <div ref={notificationRef} className="position-relative agent-notifications me-3">
              <Button
                variant="link"
                className="text-secondary p-0 position-relative"
                onClick={toggleNotifications}
                id="dropdown-notifications-agent"
                aria-expanded={showNotifications}
                title="Notifications"
              >
                <FaBell size={20} />
                {unreadCount > 0 && (
                  <Badge
                    pill
                    bg="danger"
                    className="position-absolute top-0 start-100 translate-middle border border-light"
                    style={{ fontSize: '0.65em', padding: '0.3em 0.5em' }}
                  >
                    {unreadCount > 9 ? '9+' : unreadCount}
                    <span className="visually-hidden">unread notifications</span>
                  </Badge>
                )}
              </Button>

              {/* Notification Dropdown Menu */}
              <Dropdown.Menu
                show={showNotifications}
                className="shadow mt-2 notification-dropdown"
                style={{ minWidth: '350px', maxHeight: '450px', overflowY: 'auto' }}
              >
                <Dropdown.Header className="d-flex justify-content-between align-items-center">
                  Notifications
                  {unreadCount > 0 && (
                    <Button variant="link" size="sm" onClick={handleMarkAllAsRead} className="p-0 text-decoration-none">
                      Mark all read
                    </Button>
                  )}
                </Dropdown.Header>
                <Dropdown.Divider className="my-1" />
                {loadingNotifications ? (
                  <div className="text-center p-3">
                    <Spinner animation="border" size="sm" variant="primary" /> Loading...
                  </div>
                ) : notifications.length > 0 ? (
                  <ListGroup variant="flush">
                    {notifications.map(notif => (
                      <ListGroup.Item
                        key={notif.id}
                        action={!notif.is_read}
                        onClick={() => handleMarkAsRead(notif.id)}
                        style={{
                          cursor: !notif.is_read ? 'pointer' : 'default',
                          backgroundColor: !notif.is_read ? '#f8f9fa' : 'transparent',
                          borderLeft: !notif.is_read ? '4px solid #0d6efd' : 'none',
                          padding: '0.75rem 1.25rem',
                          borderBottom: '1px solid #eee'
                        }}
                        className="notification-item"
                      >
                        <div className={`fw-bold ${!notif.is_read ? 'text-primary' : ''}`}>{notif.title || 'Notification'}</div>
                        <div className="text-muted small mb-1">{notif.message || 'No details available.'}</div>
                        <small className="text-muted">{formatTimestamp(notif.created_at)}</small>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                ) : (
                  <Dropdown.ItemText className="text-center text-muted p-3">
                    You're all caught up!
                  </Dropdown.ItemText>
                )}
              </Dropdown.Menu>
            </div>

            <div className="agent-user-profile">
              <div className="agent-avatar">
                <FaUser />
              </div>
              <div className="agent-user-info">
                <span className="agent-user-name">
                  {currentUser?.name || 'Agent User'}
                </span>
                <button className="agent-logout-btn" onClick={handleLogout}>
                  <FaSignOutAlt size={14} />
                  <span>Sign out</span>
                </button>
              </div>
            </div>
          </div>
        </header>

        <div className="dso-settings-page">
          <div className="dso-settings-content">
            {/* Left section - Navigation */}
            <div className="dso-settings-left">
              <ul className="dso-settings-nav">
                {navItems.map(item => (
                  <li key={item.id} className="dso-settings-nav-item">
                    <div 
                      className={`dso-settings-nav-link ${activeSection === item.id ? 'active' : ''}`}
                      onClick={() => setActiveSection(item.id)}
                    >
                      <span className="dso-settings-nav-icon">{item.icon}</span>
                      <span>{item.label}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {/* Right section - Content */}
            <div className="dso-settings-right">
              {renderContent()}
            </div>
          </div>
        </div>
      </div>

      {/* Reset Password Modal with Custom Styling */}
      <Modal show={showResetModal} onHide={handleCloseModal} centered className="custom-modal">
        <Form onSubmit={handlePasswordResetSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>
              <FaShieldAlt className="me-2" /> Reset Your Password
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {/* Display Error/Success Messages */}
            {resetError && <Alert variant="danger">{resetError}</Alert>}
            {resetSuccess && <Alert variant="success">{resetSuccess}</Alert>}

            {/* Hide form fields after success */}
            {!resetSuccess && (
              <>
                <Form.Group className="mb-4" controlId="formNewPassword">
                  <Form.Label>New Password</Form.Label>
                  <div className="password-input-wrapper">
                    <Form.Control
                      type="password"
                      placeholder="Enter new password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      minLength={5}
                    />
                    <FaKey className="input-icon text-muted" />
                  </div>
                  <Form.Text className="text-muted">
                    Password must be at least 5 characters long
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-4" controlId="formConfirmPassword">
                  <Form.Label>Confirm New Password</Form.Label>
                  <div className="password-input-wrapper">
                    <Form.Control
                      type="password"
                      placeholder="Confirm new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      minLength={5}
                    />
                    <FaKey className="input-icon text-muted" />
                  </div>
                </Form.Group>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal} disabled={resetLoading}>
              Cancel
            </Button>
            {/* Hide submit button after success */}
            {!resetSuccess && (
              <Button className="dso-btn dso-btn-primary" variant="" type="submit" disabled={resetLoading}>
                {resetLoading ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> Updating...
                  </>
                ) : (
                  'Update Password'
                )}
              </Button>
            )}
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}

export default AgentSettings;